import React, { useEffect, useId, useRef, useState } from "react";
import {
  createAcceptanceReview,
  getRepo,
  listAcceptanceReviews,
  preflightCompareSource,
  saveDeliveredWork,
  submitCompareSource,
} from "./api";
import { parseCompareSourceInput } from "./parseCompareSourceInput";
import { taskShortTitle } from "./taskShortTitle";
import { deliveredWorkFromReview, formatCheckedDate } from "./deliveredWorkDisplay";
import { isTargetedMode } from "./sourceComparePreflightCopy";
import { VIEWER_READ_ONLY_MESSAGE } from "./orgPermissions";
import "./ConnectDeliveredWork.css";

function errorPayload(err) {
  const data = err?.response?.data;
  return {
    message:
      data?.error ||
      data?.message ||
      err?.message ||
      "That work could not be checked. Try the pull-request or branch link again.",
    code: data?.code,
    offerNewRepository: Boolean(data?.offerNewRepository),
  };
}

function friendlyDeliveredLabel(preflight, sourceInput, repo) {
  const pull = String(sourceInput || "").match(/github\.com\/[^/]+\/[^/]+\/pull\/(\d+)/i);
  if (pull) return `${repo?.name || "Repository"} PR #${pull[1]}`;
  const ref = preflight?.headPin?.ref;
  return ref ? `${repo?.name || "Repository"} · ${ref}` : "Delivered work";
}

function creditCopy(preflight) {
  const credits =
    preflight?.estimate?.credits ??
    preflight?.estimate?.estimatedCredits ??
    preflight?.estimate?.totalCredits;
  if (credits == null) {
    return "Cost estimate unavailable. No credits will be used until you confirm.";
  }
  return `About ${credits} credits`;
}

const PROGRESS_STEPS = [
  "Confirming the exact version",
  "Reading what changed",
  "Checking each requirement",
  "Looking for tests",
  "Preparing the review",
];

export default function ConnectDeliveredWork({
  featurePlanId,
  repo,
  taskTitle = "",
  latestReview = null,
  deliveredWork = null,
  mutateAllowed = true,
  autoFocus = false,
  onReviewComplete,
  onOpenReview,
}) {
  const inputId = useId();
  const helpId = useId();
  const previewRef = useRef(null);
  const inputRef = useRef(null);
  const existing = deliveredWorkFromReview(latestReview, repo, deliveredWork ? { deliveredWork } : null);
  const [sourceInput, setSourceInput] = useState(
    existing.url || existing.originalInput || existing.branchName || ""
  );
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [progressIndex, setProgressIndex] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [useDifferent, setUseDifferent] = useState(!existing.known);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (autoFocus && inputRef.current && (useDifferent || !existing.known)) {
      inputRef.current.focus();
    }
  }, [autoFocus, useDifferent, existing.known]);

  useEffect(() => {
    if (phase !== "reviewing") return undefined;
    const timer = setInterval(() => {
      setProgressIndex((index) => Math.min(index + 1, PROGRESS_STEPS.length - 1));
    }, 1600);
    return () => clearInterval(timer);
  }, [phase]);

  const showForm =
    mutateAllowed &&
    phase !== "complete" &&
    phase !== "reviewing" &&
    (useDifferent || !existing.known || existing.status === "changed");

  const checkWork = async (event, overrideInput) => {
    event?.preventDefault?.();
    const raw = typeof overrideInput === "string" ? overrideInput : sourceInput;
    const parsed = parseCompareSourceInput(raw);
    if (!parsed.ok) {
      setError({ message: parsed.message });
      return;
    }
    setSourceInput(raw);
    setError(null);
    setPhase("checking");
    try {
      const response = await preflightCompareSource(repo._id, { sourceInput: raw.trim() });
      setPreflight(response.preflight);
      if (featurePlanId) {
        try {
          const saved = await saveDeliveredWork(featurePlanId, { sourceInput: raw.trim() });
          if (saved?.deliveredWork) {
            // Relationship is stored on the Task before any credits are used.
          }
        } catch {
          // Preview can still continue; persistence is retried when the review starts.
        }
      }
      setPhase("preview");
      window.requestAnimationFrame(() => previewRef.current?.focus?.());
    } catch (err) {
      setPhase("idle");
      setError(errorPayload(err));
    }
  };

  const runReview = async () => {
    if (!preflight || !mutateAllowed) return;
    setError(null);
    setPhase("reviewing");
    setProgressIndex(0);
    try {
      let analysisRepoId = preflight.existingAnalysis?.repoId;
      let headSha = preflight.headPin?.sha;
      const branchName = preflight.headPin?.ref;
      const baseRef = preflight.basePin?.ref;
      const baseSha = preflight.basePin?.sha;
      const analysisReady =
        preflight.existingAnalysis?.status === "completed" &&
        !preflight.existingAnalysis?.stale &&
        analysisRepoId;

      if (!analysisReady) {
        const submitted = await submitCompareSource(repo._id, {
          sourceInput: sourceInput.trim(),
          confirmed: true,
          requestedMode: preflight.mode,
          quoteToken: preflight.quoteToken,
        });
        analysisRepoId = submitted.repoId || analysisRepoId;
        if (submitted.status !== "completed") {
          for (let attempt = 0; attempt < 90; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const live = await getRepo(analysisRepoId);
            if (live.status === "completed") {
              headSha = live.analysisSource?.commitSha || live.prIntake?.head?.sha || headSha;
              break;
            }
            if (live.status === "failed") {
              throw new Error("The delivered work could not be prepared for review.");
            }
          }
        } else {
          headSha = submitted.preflight?.headPin?.sha || headSha;
        }
      }

      const data = await createAcceptanceReview(featurePlanId, {
        branchName,
        baseRef,
        headSha,
        baseSha,
        analysisRepoId,
        force: existing.stale,
      });
      setPhase("complete");
      onReviewComplete?.(data.review || null);
    } catch (err) {
      const timedOut = /timeout/i.test(err?.message || "");
      if (timedOut && featurePlanId) {
        try {
          for (let attempt = 0; attempt < 45; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const listed = await listAcceptanceReviews(featurePlanId);
            const latest = listed?.reviews?.[0];
            if (latest && (latest.status === "completed" || latest.status === "failed")) {
              setPhase(latest.status === "completed" ? "complete" : "preview");
              if (latest.status === "completed") onReviewComplete?.(latest);
              else setError({ message: "The review could not be completed." });
              return;
            }
          }
        } catch {
          // Fall through to the original timeout error.
        }
      }
      setPhase("preview");
      setError(errorPayload(err));
    }
  };

  const copyEngineerMessage = async () => {
    const title = taskShortTitle(taskTitle);
    const message = `Please send me the GitHub pull-request or branch link containing the work for: ${title}.`;
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  if (!mutateAllowed) {
    return (
      <section className="connect-delivered" data-testid="connect-delivered-work">
        <p>{VIEWER_READ_ONLY_MESSAGE}</p>
      </section>
    );
  }

  return (
    <section className="connect-delivered" data-testid="connect-delivered-work">
      <header>
        <p className="connect-delivered-kicker">Delivery</p>
        <h3>{existing.known && !useDifferent ? "Work connected to this Task" : "Paste the pull request or branch"}</h3>
        <p>
          This is where the link goes. Review is a separate tab — it shows whether the connected
          work matches this Task.
        </p>
      </header>

      {existing.known && !useDifferent ? (
        <div className="connect-delivered-current" data-testid="connect-delivered-current">
          <p data-testid="connect-delivered-label">{existing.label}</p>
          {existing.filesChanged != null ? <p>{existing.filesChanged} files changed</p> : null}
          {formatCheckedDate(existing.lastChecked) ? (
            <p>Last checked {formatCheckedDate(existing.lastChecked)}</p>
          ) : null}
          <p className="connect-delivered-status" data-status={existing.status}>
            {existing.status === "changed"
              ? "Changed since last review"
              : existing.status === "running"
                ? "Review running"
                : existing.status === "complete"
                  ? "Connected — review is on the Review tab"
                  : existing.status === "failed"
                    ? "Could not be reviewed"
                    : "Connected"}
          </p>
          {existing.status === "changed" ? (
            <p>The previous review no longer describes the latest version.</p>
          ) : null}
          <div className="connect-delivered-actions">
            <button
              type="button"
              className="connect-delivered-primary"
              data-testid="connect-delivered-review-latest"
              onClick={() => {
                const next = existing.url || existing.branchName || "";
                if (!next) {
                  setUseDifferent(true);
                  window.requestAnimationFrame(() => inputRef.current?.focus?.());
                  return;
                }
                setUseDifferent(true);
                checkWork(null, next);
              }}
            >
              {existing.status === "changed" ? "Review latest version" : "Use different branch or PR"}
            </button>
            {existing.status === "changed" ? (
              <button
                type="button"
                className="connect-delivered-secondary"
                onClick={() => {
                  setUseDifferent(true);
                  setSourceInput("");
                  window.requestAnimationFrame(() => inputRef.current?.focus?.());
                }}
              >
                Use different branch or PR
              </button>
            ) : null}
            {existing.status === "complete" && phase !== "complete" && typeof onOpenReview === "function" ? (
              <button
                type="button"
                className="connect-delivered-primary"
                data-testid="connect-delivered-open-review"
                onClick={() => onOpenReview()}
              >
                See the review
              </button>
            ) : existing.known && phase !== "complete" && typeof onOpenReview === "function" ? (
              <button
                type="button"
                className="connect-delivered-secondary"
                data-testid="connect-delivered-open-review"
                onClick={() => onOpenReview()}
              >
                See the review
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {showForm ? (
        <form onSubmit={checkWork}>
          <label htmlFor={inputId}>Pull request or branch</label>
          <p id={helpId} className="connect-delivered-help">
            Usually, your engineer will send you a pull-request link. Paste that link here.
          </p>
          <input
            id={inputId}
            ref={inputRef}
            aria-describedby={helpId}
            data-testid="connect-delivered-work-input"
            value={sourceInput}
            onChange={(event) => {
              setSourceInput(event.target.value);
              setError(null);
              setPreflight(null);
              if (phase === "preview") setPhase("idle");
            }}
            placeholder="Paste a GitHub pull-request URL, branch URL, or branch name"
            disabled={phase === "checking" || phase === "reviewing"}
          />
          {error ? (
            <p className="connect-delivered-error" role="alert" data-testid="connect-delivered-error">
              {error.message}
              {error.code === "SOURCE_REPO_MISMATCH" || /different repository/i.test(error.message || "")
                ? " This work belongs to a different repository."
                : ""}
            </p>
          ) : null}
          <div className="connect-delivered-actions">
            <button
              type="submit"
              className="connect-delivered-primary"
              data-testid="connect-delivered-check"
              disabled={phase === "checking" || phase === "reviewing" || !sourceInput.trim()}
            >
              {phase === "checking" ? "Checking…" : "Check this work"}
            </button>
            <button
              type="button"
              className="connect-delivered-secondary"
              onClick={() => setPhase((current) => (current === "no-link" ? "idle" : "no-link"))}
            >
              I don’t have a link
            </button>
          </div>
        </form>
      ) : null}

      {phase === "no-link" ? (
        <div className="connect-delivered-help-card" data-testid="connect-delivered-no-link">
          <p>Ask your engineer for the GitHub pull-request or branch link for this Task.</p>
          <button type="button" className="connect-delivered-secondary" onClick={copyEngineerMessage}>
            {copied ? "Copied" : "Copy message for engineer"}
          </button>
        </div>
      ) : null}

      {phase === "preview" && preflight ? (
        <div
          className="connect-delivered-preview"
          data-testid="connect-delivered-preview"
          tabIndex={-1}
          ref={previewRef}
        >
          <h4>You’re about to review</h4>
          <dl>
            <dt>Task</dt>
            <dd>{taskShortTitle(taskTitle)}</dd>
            <dt>Delivered work</dt>
            <dd>{friendlyDeliveredLabel(preflight, sourceInput, repo)}</dd>
            <dt>Change</dt>
            <dd>{preflight.counts?.changedFiles ?? 0} files changed</dd>
            <dt>Comparison</dt>
            <dd>
              {preflight.basePin?.ref || "default branch"} → {preflight.headPin?.ref || "delivered branch"}
            </dd>
            <dt>Analysis</dt>
            <dd>
              {isTargetedMode(preflight.mode)
                ? "Only the changed and closely related files will be checked."
                : "RenderRepo will check the delivered work against this Task."}
            </dd>
            <dt>Estimated cost</dt>
            <dd>{creditCopy(preflight)}</dd>
          </dl>
          <div className="connect-delivered-actions">
            <button
              type="button"
              className="connect-delivered-primary"
              data-testid="connect-delivered-confirm"
              onClick={runReview}
            >
              Review this work
            </button>
            <button
              type="button"
              className="connect-delivered-secondary"
              onClick={() => {
                setPreflight(null);
                setPhase("idle");
                inputRef.current?.focus?.();
              }}
            >
              Choose different work
            </button>
          </div>
          <details
            className="connect-delivered-advanced"
            open={showAdvanced}
            onToggle={(event) => setShowAdvanced(event.currentTarget.open)}
          >
            <summary>Technical details</summary>
            {showAdvanced ? (
              <>
                <p>Head {preflight.headPin?.sha || "unresolved"}</p>
                <p>Base {preflight.basePin?.sha || "unresolved"}</p>
              </>
            ) : null}
          </details>
        </div>
      ) : null}

      {phase === "reviewing" ? (
        <div className="connect-delivered-progress" data-testid="connect-delivered-progress" role="status">
          <h4>Reviewing the work</h4>
          <ol>
            {PROGRESS_STEPS.map((step, index) => (
              <li key={step} data-current={index === progressIndex || undefined}>
                {step}
              </li>
            ))}
          </ol>
          <p>You can leave this page. We’ll keep working.</p>
        </div>
      ) : null}

      {phase === "complete" ? (
        <div className="connect-delivered-preview" data-testid="connect-delivered-complete">
          <h4>Work connected</h4>
          <p>The review of this work is on the Review tab.</p>
          {typeof onOpenReview === "function" ? (
            <button
              type="button"
              className="connect-delivered-primary"
              data-testid="connect-delivered-open-review"
              onClick={() => onOpenReview()}
            >
              See the review
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
